import { defineMessages } from "react-intl";

import {
	aorticInsufficiencyMessages,
	DiastolicDysfunction,
	diastolicDysfunctionMessages,
	mitralInsufficiencyMessages,
	tricuspidInsufficiencyMessages,
	validator
} from "lib/build/forms/htx/current-cardiological-state/echocardiography";

import { FieldData, FieldsetData } from "src/features/forms";
import { genericErrorMessages } from "src/messages/generic";
import { LeafNode } from "src/modules/shared/form-explorer";

const messages = defineMessages({
	echocardiography: {
		id: "form.echocardiography",
		defaultMessage: "Echokardiográfia"
	},
	ejectionFraction: {
		id: "form.echocardiography.ejection_fraction",
		defaultMessage: "Ejekciós frakció"
	},
	edd: {
		id: "form.echocardiography.edd",
		defaultMessage: "EDD"
	},
	swt: {
		id: "form.echocardiography.swt",
		defaultMessage: "SWT"
	},
	pwt: {
		id: "form.echocardiography.pwt",
		defaultMessage: "PWT"
	},
	laDiameter1: {
		id: "form.echocardiography.la_diameter1",
		defaultMessage: "LA diameter1"
	},
	laDiameter2: {
		id: "form.echocardiography.la_diameter2",
		defaultMessage: "LA diameter2"
	},
	raDiameter1: {
		id: "form.echocardiography.ra_diameter1",
		defaultMessage: "RA diameter1"
	},
	raDiameter2: {
		id: "form.echocardiography.ra_diameter2",
		defaultMessage: "RA diameter2"
	},
	aorticRootSize: {
		id: "form.echocardiography.aortic_root_size",
		defaultMessage: "Aorta gyök"
	},
	aortaAscendensSize: {
		id: "form.echocardiography.aorta_ascendens_size",
		defaultMessage: "Aorta ascendens"
	},
	rvSize: {
		id: "form.echocardiography.rv_size",
		defaultMessage: "RV"
	},
	tapse: {
		id: "form.echocardiography.tapse",
		defaultMessage: "TAPSE"
	},
	diastolicDysfunction: {
		id: "form.echocardiography.diastolic_dysfunction",
		defaultMessage: "Diasztolés diszfunkció"
	},
	mitralInsufficiency: {
		id: "form.echocardiography.mitral_insufficiency",
		defaultMessage: "Mitrális inszufficiencia"
	},
	aorticInsufficiency: {
		id: "form.echocardiography.aortic_insufficiency",
		defaultMessage: "Aorta inszufficiencia"
	},
	aorticValveGradient: {
		id: "form.echocardiography.aortic_valve_gradient",
		defaultMessage: "Aorta gradiens"
	},
	tricuspidInsufficiency: {
		id: "form.echocardiography.tricuspid_insufficiency",
		defaultMessage: "Tricuspidális inszufficiencia"
	},
	estimatedRightVentricularPeakSystolicPressure: {
		id: "form.echocardiography.estimated_right_ventricular_peak_systolic_pressure",
		defaultMessage: "Kalkulált kivérköri nyomás"
	},
	description: {
		id: "form.echocardiography.comment",
		defaultMessage: "Megjegyzés"
	},
	[DiastolicDysfunction.YES]: {
		id: "form.echocardiography.diastolic_dysfunction.yes",
		defaultMessage: "Van"
	},
	[DiastolicDysfunction.NO]: {
		id: "form.echocardiography.diastolic_dysfunction.no",
		defaultMessage: "Nincs"
	},
	[DiastolicDysfunction.NOT_MEASURED]: {
		id: "form.echocardiography.diastolic_dysfunction.not_measured",
		defaultMessage: "Nem vizsgált"
	}
});
const fields: { [key: string]: FieldData } = {
	ejectionFraction: {
		type: "field",
		path: ["ejectionFraction"],
		label: messages.ejectionFraction,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	edd: {
		type: "field",
		path: ["edd"],
		label: messages.edd,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	swt: {
		type: "field",
		path: ["swt"],
		label: messages.swt,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	pwt: {
		type: "field",
		path: ["pwt"],
		label: messages.pwt,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	laDiameter1: {
		type: "field",
		path: ["laDiameter1"],
		label: messages.laDiameter1,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	laDiameter2: {
		type: "field",
		path: ["laDiameter2"],
		label: messages.laDiameter2,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	raDiameter1: {
		type: "field",
		path: ["raDiameter1"],
		label: messages.raDiameter1,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	raDiameter2: {
		type: "field",
		path: ["raDiameter2"],
		label: messages.raDiameter2,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	aorticRootSize: {
		type: "field",
		path: ["aorticRootSize"],
		label: messages.aorticRootSize,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	aortaAscendensSize: {
		type: "field",
		path: ["aortaAscendensSize"],
		label: messages.aortaAscendensSize,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	rvSize: {
		type: "field",
		path: ["rvSize"],
		label: messages.rvSize,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	tapse: {
		type: "field",
		path: ["tapse"],
		label: messages.tapse,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	diastolicDysfunction: {
		type: "field",
		path: ["diastolicDysfunction"],
		label: messages.diastolicDysfunction,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: diastolicDysfunctionMessages }
		}
	},
	mitralInsufficiency: {
		type: "field",
		path: ["mitralInsufficiency"],
		label: messages.mitralInsufficiency,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: mitralInsufficiencyMessages }
		}
	},
	aorticInsufficiency: {
		type: "field",
		path: ["aorticInsufficiency"],
		label: messages.aorticInsufficiency,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: aorticInsufficiencyMessages }
		}
	},
	aorticValveGradient: {
		type: "field",
		path: ["aorticValveGradient"],
		label: messages.aorticValveGradient,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	tricuspidInsufficiency: {
		type: "field",
		path: ["tricuspidInsufficiency"],
		label: messages.tricuspidInsufficiency,
		errorMessages: genericErrorMessages,
		input: {
			type: "button-group",
			props: { labels: tricuspidInsufficiencyMessages }
		}
	},
	estimatedRightVentricularPeakSystolicPressure: {
		type: "field",
		path: ["estimatedRightVentricularPeakSystolicPressure"],
		label: messages.estimatedRightVentricularPeakSystolicPressure,
		errorMessages: genericErrorMessages,
		input: { type: "input", props: { type: "number" } }
	},
	description: {
		type: "field",
		path: ["description"],
		label: messages.description,
		errorMessages: genericErrorMessages,
		input: { type: "textarea", props: {} }
	}
};

const fieldset: FieldsetData = {
	rows: [
		{
			columns: [
				{ width: 6, field: fields.ejectionFraction },
				{ width: 6, field: fields.edd }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.swt },
				{ width: 6, field: fields.pwt }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.laDiameter1 },
				{ width: 6, field: fields.laDiameter2 }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.raDiameter1 },
				{ width: 6, field: fields.raDiameter2 }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.aorticRootSize },
				{ width: 6, field: fields.aortaAscendensSize }
			]
		},
		{
			columns: [
				{ width: 6, field: fields.rvSize },
				{ width: 6, field: fields.tapse }
			]
		},
		{ columns: [{ width: 12, field: fields.diastolicDysfunction }] },
		{ columns: [{ width: 12, field: fields.mitralInsufficiency }] },
		{ columns: [{ width: 12, field: fields.aorticInsufficiency }] },
		{
			columns: [
				{ width: 6, field: fields.aorticValveGradient },
				{
					width: 6,
					field: fields.estimatedRightVentricularPeakSystolicPressure
				}
			]
		},
		{ columns: [{ width: 12, field: fields.tricuspidInsufficiency }] },
		{ columns: [{ width: 12, field: fields.description }] }
	]
};

export const node: LeafNode = {
	id: "echocardiography",
	value: {
		title: messages.echocardiography,
		validator,
		fieldset,
		isImportant: true
	}
};
